import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { Download, FileUp, RefreshCw } from 'lucide-react';
import { AssistantIntakeInput, assistantRequestId, delegationActive, validateMappedCsv } from '../../services/operatorAssistant';
import { useOperatorAssistant } from './OperatorAssistantContext';
import { StatusBadge } from './AssistantCards';

const sample = 'key,operation,data_json,account_key\naccount:example,accounts_create,"{""name"":""Example organization""}",\ncustomer:example,customers_create,"{""email"":""contact@example.com"",""firstName"":""Example"",""lastName"":""Contact""}",account:example\n';
export default function AssistantIntake({ onReview }: { onReview: (id: string) => void }) {
  const assistant = useOperatorAssistant();
  const { source, csv, filename, error, busy, pending, receipt } = assistant.intake;
  const [reading, setReading] = useState(false);
  const mounted = useRef(true);
  const fileVersion = useRef(0);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const readFile = async (file?: File) => {
    const version = ++fileVersion.current;
    assistant.updateIntake({ error: '', csv: '', filename: '', receipt: null });
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv') || file.size > 7200000) {
      assistant.updateIntake({ error: 'Choose a UTF-8 CSV file within the 1,800,000-character limit. XLSX and PDF are not supported here.' }); return;
    }
    setReading(true);
    try {
      const text = new TextDecoder('utf-8', { fatal: true }).decode(await file.arrayBuffer()).replace(/^\uFEFF/, '');
      if (!mounted.current || fileVersion.current !== version) return;
      const validation = validateMappedCsv(text);
      if (validation) { assistant.updateIntake({ error: validation }); return; }
      assistant.updateIntake({ csv: text, filename: file.name });
    } catch {
      if (mounted.current && fileVersion.current === version) assistant.updateIntake({ error: 'The file could not be read as UTF-8 CSV.' });
    } finally { if (mounted.current && fileVersion.current === version) setReading(false); }
  };
  const send = async (input: AssistantIntakeInput) => {
    const action = await assistant.prepareIntake(input);
    if (mounted.current && action) onReview(action.id);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (pending || !csv || !source.trim() || !assistant.configuration?.enabled) return;
    const input: AssistantIntakeInput = { source: source.trim(), csv, request_id: assistantRequestId() };
    if (assistant.delegationId && assistant.delegations.some((rule) => rule.id === assistant.delegationId && delegationActive(rule))) {
      input.delegation_id = assistant.delegationId;
    }
    await send(input);
  };
  return <section className="assistant-intake" aria-label="Mapped CSV intake">
    <div className="assistant-section-heading"><h2>Mapped CSV intake</h2><a href={`data:text/csv;charset=utf-8,${encodeURIComponent(sample)}`} download="modal-intake-template.csv"><Download size={16} aria-hidden="true" />CSV template</a></div>
    <form onSubmit={submit}>
      <label>Source namespace<input value={source} onChange={(event) => assistant.updateIntake({ source: event.target.value })} required maxLength={120} disabled={busy || !!pending} placeholder="elegant-sept-reservations" /></label>
      <p className="assistant-muted">Corrections retain the same source namespace and row keys.</p>
      <label>Mapped UTF-8 CSV<input type="file" accept=".csv,text/csv" onChange={(event) => void readFile(event.target.files?.[0])} disabled={busy || !!pending} /></label>
      <div className="assistant-csv-columns"><span>Required</span><code>key</code><code>operation</code><code>data_json</code><span>Optional</span><code>existing_id</code><code>account_key</code><code>customer_key</code><code>reservation_key</code></div>
      {reading && <p role="status">Reading CSV...</p>}
      {filename && <p>{filename} - {csv.length.toLocaleString()} characters</p>}
      {error && <p role="alert" className="assistant-notice assistant-notice-error">{error}</p>}
      <button type="submit" disabled={busy || reading || !!pending || !csv || !source.trim() || !assistant.configuration?.enabled} className="assistant-primary"><FileUp size={16} aria-hidden="true" />{busy ? 'Validating mapped rows...' : 'Prepare intake review'}</button>
      {pending && !busy && <div className="assistant-review-controls"><button type="button" onClick={() => void assistant.refresh()}><RefreshCw size={16} aria-hidden="true" />Check receipts</button><button type="button" onClick={() => void send(pending)}>Retry same intake request</button></div>}
      {receipt && <div className="assistant-intake-receipt"><StatusBadge status={receipt.status} /><span>{receipt.id}</span><button type="button" onClick={() => onReview(receipt.id)}>Inspect intake review</button></div>}
    </form>
  </section>;
}
